import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { StatusCodes } from 'http-status-codes'

import { AuthEvent } from '@/auth/authInitState'
import { keycloakAuth } from '@/auth/keycloak'
import { env } from '@/config/env'
import { logger } from '@/utils/logger'

import { createReauthBaseQuery } from './reauthBaseQuery'

const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.apiBaseUrl,
  prepareHeaders: (headers, { getState: _getState }) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken')
    logger.log('[localStorage] authToken:', token ? 'found' : 'not found')
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    return headers
  },
})

enum ApiErrorCode {
  UserDisabled = 'user_disabled',
}

function isUserDisabledError(error: FetchBaseQueryError): boolean {
  if (
    error.status === StatusCodes.FORBIDDEN &&
    error.data &&
    typeof error.data === 'object'
  ) {
    const extra = (error.data as { extra?: { code?: ApiErrorCode } }).extra
    return extra?.code === ApiErrorCode.UserDisabled
  }
  return false
}

const loggingBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const url = typeof args === 'string' ? args : args.url
  const method = (
    typeof args === 'string' ? 'GET' : (args.method ?? 'GET')
  ).toUpperCase()
  logger.log(`[server] ${method} ${url}`)
  const result = await rawBaseQuery(args, api, extraOptions)
  if (result.error) {
    logger.error(`[server] ${method} ${url} → error`, result.error)
    if (isUserDisabledError(result.error)) {
      localStorage.removeItem('authToken')
      window.dispatchEvent(new CustomEvent(AuthEvent.UserDisabled))
    }
  } else {
    logger.log(`[server] ${method} ${url} → ok`)
  }
  return result
}

const baseQueryWithReauth = createReauthBaseQuery(loggingBaseQuery, () =>
  keycloakAuth.forceTokenRefresh(),
)

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'User',
    'Account',
    'Role',
    'Organization',
    'AccountUser',
    'FeatureFlag',
    'BulkImport',
  ],
  endpoints: () => ({}),
})
